import { Link, Outlet, useLocation, useNavigate, useOutletContext, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, ChevronRight } from 'lucide-react';
import type { Property } from '@vuokramani/shared';
import { Tabs, pageStyles as s } from '../../components/page.tsx';
import { Button, EmptyState, Splash } from '../../components/ui.tsx';
import {
  propertyTitle,
  useArchiveProperty,
  useProperties,
  useProperty,
} from '../../lib/properties.ts';

/**
 * Yksittäisen asunnon sivu. Murupolku ja tunnistekortti ovat aina näkyvissä, jotta on
 * selvää, minkä asunnon tietoja käsitellään. Asunnon voi vaihtaa kortin valitsimesta,
 * jolloin pysytään samalla välilehdellä.
 */
export function PropertyLayout() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: property, isLoading, isError } = useProperty(id);
  const { data: all } = useProperties('owner');
  const archive = useArchiveProperty(id ?? '');

  if (isLoading) return <Splash />;
  if (isError || !property) {
    return <EmptyState icon={<Building2 size={22} />} title={t('property.notFound')} />;
  }

  const base = `/vuokranantaja/asunnot/${property.id}`;
  const currentTab = location.pathname.slice(base.length); // esim. "/talous"

  const meta = [
    property.name ? property.streetAddress : null,
    `${property.postalCode} ${property.city}`,
    property.buildingType
      ? t(`buildingTypes.${property.buildingType}`)
      : t(`propertyTypes.${property.type}`),
    property.areaM2 ? `${property.areaM2.toLocaleString(i18n.language)} m²` : null,
  ].filter(Boolean);

  return (
    <>
      <nav className={s.breadcrumb} style={{ marginBottom: '0.9rem' }} aria-label="breadcrumb">
        <Link to="/vuokranantaja/asunnot">{t('property.breadcrumb')}</Link>
        <ChevronRight size={14} aria-hidden="true" />
        <span aria-current="page">{propertyTitle(property)}</span>
      </nav>

      {property.archivedAt && (
        <div className={s.banner} role="status">
          <span>{t('archive.archivedBanner')}</span>
          <Button onClick={() => archive.mutate('restore')} disabled={archive.isPending}>
            {t('archive.restore')}
          </Button>
        </div>
      )}

      <div className={s.identity}>
        <div className={s.identityText}>
          <h1 className={s.identityTitle}>{propertyTitle(property)}</h1>
          <div className={s.identityMeta}>
            {meta.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </div>
        </div>
        {all && all.length > 1 && (
          <label className={s.switcher}>
            <span className="visually-hidden">{t('property.switch')}</span>
            <select
              className={s.switcherSelect}
              value={property.id}
              onChange={(e) =>
                void navigate(`/vuokranantaja/asunnot/${e.target.value}${currentTab}`)
              }
            >
              {all.map((p) => (
                <option key={p.id} value={p.id}>
                  {propertyTitle(p)}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className={s.switcherIcon} aria-hidden="true" />
          </label>
        )}
      </div>

      <Tabs
        label={propertyTitle(property)}
        items={[
          { to: base, label: t('property.tabs.overview'), end: true },
          { to: `${base}/vuokrasuhde`, label: t('property.tabs.tenancy') },
          { to: `${base}/tarkastus`, label: t('property.tabs.inspection') },
          { to: `${base}/talous`, label: t('property.tabs.finance') },
          { to: `${base}/huolto`, label: t('property.tabs.maintenance') },
          { to: `${base}/tiedostot`, label: t('property.tabs.files') },
          { to: `${base}/viestit`, label: t('property.tabs.messages') },
          { to: `${base}/asetukset`, label: t('property.tabs.settings') },
        ]}
      />

      <Outlet context={property} />
    </>
  );
}

export function useCurrentProperty(): Property {
  return useOutletContext<Property>();
}
